import React, { useState, useRef } from 'react';
import { Box, Button, CircularProgress, Typography, LinearProgress } from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { useCampaigns } from '../hooks/useCampaigns';

interface VideoUploadProps {
    campaignId: string;
    onUploadComplete: (videoUrl: string) => void;
}

const VideoUpload: React.FC<VideoUploadProps> = ({ campaignId, onUploadComplete }) => {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { uploadVideo } = useCampaigns();

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Reset states
        setError(null);
        setUploading(true);
        setUploadProgress(0);

        try {
            // Validate file type
            if (!file.type.startsWith('video/')) {
                throw new Error('Please select a video file');
            }

            // Validate file size (max 100MB)
            const maxSize = 100 * 1024 * 1024; // 100MB in bytes
            if (file.size > maxSize) {
                throw new Error('Video size should be less than 100MB');
            }

            // Upload video with progress tracking
            const videoUrl = await uploadVideo(file, campaignId, (progress) => {
                setUploadProgress(progress);
            });

            if (!videoUrl) {
                throw new Error('Failed to get video URL after upload');
            }

            onUploadComplete(videoUrl);
            setError(null);
        } catch (err) {
            console.error('Video upload error:', err);
            setError(err instanceof Error ? err.message : 'Failed to upload video');
        } finally {
            setUploading(false);
            setUploadProgress(0);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    return (
        <Box sx={{ textAlign: 'center', p: 2 }}>
            <input
                type="file"
                accept="video/*"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleFileSelect}
            />
            <Button
                variant="outlined"
                startIcon={uploading ? <CircularProgress size={20} /> : <CloudUpload />}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                sx={{ mb: 1 }}
            >
                {uploading ? `Uploading ${Math.round(uploadProgress * 100)}%` : 'Upload Video'}
            </Button>
            {uploading && (
                <Box sx={{ width: '100%', mt: 1 }}>
                    <LinearProgress variant="determinate" value={uploadProgress * 100} />
                </Box>
            )}
            {error && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
};

export default VideoUpload; 